import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { NetworkOffline } from './NetworkOffline';
import { Button } from '../ui/Button';
import { NETWORK_ERROR_EVENT } from '../../lib/query-client';
import { resolveApiRoot } from '../../lib/api-fetcher';

type OfflinePhase = 'online' | 'overlay' | 'banner';

interface OfflineContextValue {
  isOffline: boolean;
  isBannerVisible: boolean;
  retryConnection: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextValue>({
  isOffline: false,
  isBannerVisible: false,
  retryConnection: async () => {},
});

export function useOfflineStatus() {
  return useContext(OfflineContext);
}

const POLL_INTERVAL = 30_000;

async function checkConnectivity(): Promise<boolean> {
  if (!navigator.onLine) return false;
  try {
    const apiRoot = resolveApiRoot();
    const res = await fetch(`${apiRoot}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
    // allow-silent-catch: expected outcome of a connectivity probe (polled every 30s while offline); the boolean return is the signal, and logging every failed poll would spam the console.
  } catch {
    return false;
  }
}

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<OfflinePhase>('online');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const retryConnection = useCallback(async () => {
    const ok = await checkConnectivity();
    if (ok) {
      stopPolling();
      setPhase('online');
    }
  }, [stopPolling]);

  const goOffline = useCallback(() => {
    setPhase((p) => (p === 'online' ? 'overlay' : p));
  }, []);

  const dismissOverlay = useCallback(() => {
    setPhase('banner');
  }, []);

  useEffect(() => {
    if (!navigator.onLine) {
      goOffline();
    }
  }, [goOffline]);

  useEffect(() => {
    const handleOnline = () => retryConnection();
    const handleOffline = () => goOffline();
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [retryConnection, goOffline]);

  useEffect(() => {
    const handler = () => goOffline();
    window.addEventListener(NETWORK_ERROR_EVENT, handler);
    return () => window.removeEventListener(NETWORK_ERROR_EVENT, handler);
  }, [goOffline]);

  useEffect(() => {
    if (phase !== 'online' && !pollRef.current) {
      pollRef.current = setInterval(() => {
        retryConnection();
      }, POLL_INTERVAL);
    }
    if (phase === 'online') {
      stopPolling();
    }
    return stopPolling;
  }, [phase, retryConnection, stopPolling]);

  const ctx: OfflineContextValue = {
    isOffline: phase !== 'online',
    isBannerVisible: phase === 'banner',
    retryConnection,
  };

  return (
    <OfflineContext.Provider value={ctx}>
      {children}
      {phase === 'overlay' && (
        <div className="fixed inset-0 z-toast bg-bg">
          <NetworkOffline onRetry={retryConnection} onDismiss={dismissOverlay} />
        </div>
      )}
      {phase === 'banner' && (
        <div className="fixed top-0 left-0 right-0 z-toast bg-warning-bg text-warning-text border-b border-warning-500/30 px-4 py-2.5 flex items-center justify-center gap-3 text-sm shadow-sm">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <path d="M8.5 2H15l7 7v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h4.5" />
            <path d="M15 2v7h7" />
            <path d="M9 15a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1z" />
            <path d="M12 10v2" />
          </svg>
          <span className="font-medium">No connection</span>
          <span className="text-warning-text/70">—</span>
          <span>Data shown may be out of date</span>
          <Button variant="ghost" size="sm" onClick={retryConnection}>
            Retry
          </Button>
        </div>
      )}
    </OfflineContext.Provider>
  );
}
